/**
 * The outreach mailboxes with their send count against today's warm-up cap,
 * and the global kill switch. The kill switch stays reachable while the
 * mailbox list loads or fails to load. Presentational – the data and the
 * mutations come from `useSenderControls`.
 */
import { useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { clsx } from "clsx";

import type { OutreachSender } from "../../api/b2g";
import { formatRelative } from "../../lib/formatRelative";
import { useConfirm } from "../confirm/context";
import { ErrorStrip } from "../QueryBoundary";

export function SenderBar({
  query,
  killing,
  registering,
  onKill,
  onRegister,
}: {
  query: UseQueryResult<OutreachSender[]>;
  killing: boolean;
  registering: boolean;
  onKill: (paused: boolean, reason?: string) => void;
  onRegister: (address: string) => void;
}) {
  const [address, setAddress] = useState("");
  const senders = query.data;

  // The kill switch is global (POST /v1/outreach/kill-switch needs no sender
  // list), so it stays reachable while the list loads or fails to load.
  if (!senders) {
    return (
      <div className="mb-5 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[16rem]">
          {query.isError ? (
            <ErrorStrip
              message="Couldn't load the outreach mailboxes – the sending state is unknown."
              onRetry={() => void query.refetch()}
              retrying={query.isFetching}
            />
          ) : (
            <div className="text-sm text-ink-low" aria-busy="true">Loading outreach mailboxes…</div>
          )}
        </div>
        <KillButton paused={false} killing={killing} onKill={onKill} />
      </div>
    );
  }
  if (senders.length === 0) {
    return (
      <div className="panel mb-5">
        <div className="text-sm mb-2">
          No outreach mailbox registered yet. Connect the shared mailbox first (Jarvis: mailbox_connect, exchange_shared),
          then register it here – warm-up starts with the registration.
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            aria-label="Outreach mailbox address"
            className="input flex-1"
            placeholder="outreach@your-domain.com"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-sm btn-second"
            disabled={registering || !address.includes("@")}
            onClick={() => onRegister(address.trim())}
          >
            Register mailbox
          </button>
        </div>
      </div>
    );
  }
  const paused = senders.some((s) => s.paused);
  return (
    <div className="mb-5">
      {query.isError ? (
        <div role="status" className="callout callout--warn mb-2 text-xs">
          Couldn't refresh the mailboxes – showing the last known state.
        </div>
      ) : null}
      <div className={clsx("panel flex items-center gap-4 flex-wrap", paused && "panel--danger")}>
        {senders.map((s) => (
          <div key={s.id} className="text-sm">
            <span className="font-semibold">{s.email_address}</span>
            <span className="text-ink-low">
              {" · "}
              {s.sent_today}/{s.cap_today} today
              {s.last_sent_at ? ` · last send ${formatRelative(s.last_sent_at)}` : ""}
            </span>
            {s.paused ? (
              <div className="text-xs text-danger">
                Paused: {s.paused_reason ?? "kill switch"}
              </div>
            ) : null}
          </div>
        ))}
        <div className="ml-auto">
          <KillButton paused={paused} killing={killing} onKill={onKill} />
        </div>
      </div>
    </div>
  );
}

function KillButton({
  paused,
  killing,
  onKill,
}: {
  paused: boolean;
  killing: boolean;
  onKill: (paused: boolean, reason?: string) => void;
}) {
  const confirm = useConfirm();
  if (paused) {
    return (
      <button type="button" className="btn btn-sm btn-second" disabled={killing} onClick={() => onKill(false)}>
        Resume sending
      </button>
    );
  }
  return (
    <button
      type="button"
      className="btn btn-sm btn-ghost btn-danger"
      disabled={killing}
      onClick={async () => {
        const ok = await confirm({
          title: "Stop all outreach sending now?",
          body: "Approved waves wait until you resume.",
          confirmLabel: "Stop sending",
          tone: "danger",
        });
        if (ok) onKill(true, "stopped in the WebUI");
      }}
    >
      Stop all sending
    </button>
  );
}
